import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import Highlighter from "react-highlight-words";
import { KeyCode } from "./Suggestions";
import Tooltip from "../../../../components/Tooltip/Tooltip";

export const NO_RESULTS_LABEL = "No result";

interface SuggestionsListProps {
  items: string[];
  command: (item: string) => void;
  searchValue: string;
  showFullValueTooltip?: boolean;
}

export interface SuggestionsListHandle {
  onKeyDown: (params: { event: KeyboardEvent }) => boolean;
}

const SuggestionsList = forwardRef<SuggestionsListHandle, SuggestionsListProps>(
  ({ items, command, searchValue, showFullValueTooltip = false }, ref) => {
    const [focusedIndex, setFocusedIndex] = useState(0);
    const itemRefs = React.useRef<Array<HTMLLIElement | null>>([]);
    const focusedIndexRef = React.useRef(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (!item) {
        return;
      }
      command(item);
    };

    const upHandler = () => {
      if (!items.length) {
        return;
      }
      setFocusedIndex(
        (prev) => (prev + items.length - 1) % Math.max(items.length, 1)
      );
    };

    const downHandler = () => {
      if (!items.length) {
        return;
      }
      setFocusedIndex((prev) => (prev + 1) % Math.max(items.length, 1));
    };

    const enterHandler = () => {
      selectItem(focusedIndexRef.current);
    };

    useEffect(() => {
      setFocusedIndex(0);
    }, [items]);

    useEffect(() => {
      focusedIndexRef.current = focusedIndex;
      itemRefs.current[focusedIndex]?.scrollIntoView({
        block: "nearest",
      });
    }, [focusedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === KeyCode.ArrowUp) {
          upHandler();
          return true;
        }

        if (event.key === KeyCode.ArrowDown) {
          downHandler();
          return true;
        }

        if (event.key === KeyCode.Enter) {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    return (
      <ul className="dropdown-menu">
        {items.length ? (
          items.map((item, index) => {
            const listItem = (
              <li
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                className={index === focusedIndex ? "is-focused" : ""}
                key={`${item}-${index}`}
                onClick={() => selectItem(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === KeyCode.Enter || e.key === " ") {
                    e.preventDefault();
                    selectItem(index);
                  }
                }}
                onMouseEnter={() => {
                  setFocusedIndex(index);
                }}
              >
                <Highlighter
                  highlightClassName={"highlight-text-wrap"}
                  searchWords={[searchValue]}
                  autoEscape={true}
                  textToHighlight={item}
                />
              </li>
            );

            return showFullValueTooltip ? (
              <Tooltip
                key={`${item}-${index}`}
                content={item}
                placement="right"
                showOnOverflow={true}
              >
                {listItem}
              </Tooltip>
            ) : (
              listItem
            );
          })
        ) : (
          <li className="item">{NO_RESULTS_LABEL}</li>
        )}
      </ul>
    );
  }
);

export default SuggestionsList;
